import { INewsItem } from "@/types/news";
import React from "react";
import Image from "next/image";
import { getDaySuffix } from "@/utils";
import Link from "next/link";
import { convertNameToSlug } from "@/utils/queryUtils/queryUtils";

interface NewsItemProps {
  item: INewsItem;
  isLastItem?: boolean;
  locale: string;
}
/**
 * NewsItem Component
 * --------------------------------------------------
 * Renders a single news list entry with:
 *  - Formatted date (with day suffix)
 *  - Title linked to either an external URL or internal news detail page
 *  - Optional thumbnail image
 *  - Styled responsive layout with border control for last items
 */
export const NewsItem: React.FC<NewsItemProps> = ({
  item,
  isLastItem,
  locale,
}) => {
  const date = new Date(item?.createdAt);
  const day = date.getUTCDate();
  const hotelNames = item?.properties?.hotel_names;
  const header = hotelNames
    ? `${hotelNames} ${item?.properties?.header_title}`
    : item?.properties?.header_title;
  const externalLink = item?.properties?.external_link;
  const image = item?.properties?.featured_image?.url;
  const daySuffix = getDaySuffix(day);

  return (
    <div
      className={`border-b border-b-[var(--stroke-gray-normal)] py-6 md:py-8 ${isLastItem && "border-b-0"}`}
    >
      <Link
        href={
          externalLink && externalLink !== "#"
            ? externalLink
            : `/${locale}/news-${convertNameToSlug(item.item_name)}`
        }
        target="_blank"
        className="flex min-h-[173px] flex-row items-start"
      >
        <div className="flex w-2/3 flex-col md:w-5/6 md:flex-row md:items-center md:pt-4">
          {/* Date */}
          <div className="flex md:w-1/10 md:pr-6">
            <div className="text-2xl font-[var(--font-bold)] md:text-3xl">
              {day}
            </div>
            <div className="ml-1 text-sm text-[var(--fill-gray-stronger)] md:text-xl">
              {daySuffix}
            </div>
          </div>

          {/* Content */}
          <div className="flex-grow pt-2 pr-4 md:min-h-[96px] md:w-9/10 md:border-l md:border-l-[var(--stroke-gray-normal)] md:pt-0 md:pr-22 md:pl-8">
            {header && (
              <h2 className="text-sm font-[var(--font-bold)] md:text-2xl">
                {header}
              </h2>
            )}
          </div>
        </div>

        {/* Image */}
        {image && (
          <div className="w-1/3 md:w-[29.2%]">
            <div className="relative aspect-[5/3]">
              <Image
                src={image || ""}
                alt={item.properties.featured_image?.alternativeText || ""}
                aria-label={item.properties.featured_image?.caption || ""}
                className={`w-full rounded object-cover`}
                width={500}
                height={300}
                priority
              />
            </div>
          </div>
        )}
      </Link>
    </div>
  );
};
